<template>
	<div class="list-container column">
		<div class="title-container" :class="{ mobile: isMobile }">
			<h1 class="title">{{ $t('file_list.title') }}</h1>
			<div class="field">
				<div class="template-wrapper mb-1">
					<div class="template-wrapper__header">
						{{ $t('file_list.templates') }}
					</div>
					<button class="button is-info" @click.prevent="downloadLocalized('policy')">
						{{ $t('file_list.template_fire_policy') }} <i class="fas fa-download"></i>
					</button>
					<button class="button is-info" @click.prevent="downloadLocalized('rules')">
						{{ $t('file_list.template_rules') }} <i class="fas fa-download"></i>
					</button>
					<button class="button is-info" @click.prevent="downloadLocalized('business')">
						{{ $t('file_list.template_business') }} <i class="fas fa-download"></i>
					</button>
				</div>
				<div class="file has-name is-fullwidth is-dark mb-1" v-if="$isAllowedTo('documents_upload')">
					<label class="file-label">
						<input
							ref="fileupload"
							class="file-input"
							type="file"
							name="resume"
							accept=".doc, .docx, .pdf, .xlsx, .xls"
							@change="uploadFile"
						/>
						<span class="file-cta">
							<span class="file-icon">
								<i class="fas fa-upload"></i>
							</span>
							<span class="file-label">
								{{ $t('file_list.choose_file') }}
							</span>
						</span>
						<span class="file-name">
							{{ fileName }}
						</span>
					</label>
					<button
						class="button is-info"
						:disabled="file === null"
						@click.prevent="saveFile"
					>
						<span class="icon"><i class="fas fa-plus"/></span>
					</button>
				</div>
			</div>
		</div>
		<div class="panel-list-header">
			<div class="panel-header-item">{{ $t('file_list.name') }}</div>
			<div v-if="!isMobile" class="panel-header-item">{{ $t('file_list.created') }}</div>
			<div class="panel-filler" v-if="$isAllowedTo('files_edit')" />
			<div class="panel-filler" v-if="$isAllowedTo('files_edit')" />
		</div>
		<div
			class="panel-list"
			v-for="file in files"
			:key="file.id"
			@click.stop="onSelect(file)"
		>
			<div class="panel-list-item">{{ file.name }}</div>
			<div v-if="!isMobile" class="panel-list-item">
				{{ file.created.substring(0, 10) }}
			</div>
			<div class="list-button" v-if="$isAllowedTo('files_edit')" @click.stop="onSelect(file)">
				<i class="fas fa-download"></i>
			</div>
			<div
				v-if="file && $isAllowedTo('files_edit')"
				class="list-button"
				@click.stop="onDelete(file)"
			>
				<i class="fas fa-trash-alt"></i>
			</div>
		</div>
		<div class="panel-list no-hover" v-if="files.length == 0">
			<p class="panel-list-item">{{ $t('file_list.no_results') }}</p>
		</div>
		<Pagination
			v-if="files.length > 0"
			:pagination="pagination"
			@paginate="paginate"
			@updatePageSize="updatePageSize"
		/>
	</div>
</template>

<script>
import Pagination from '@/components/Pagination';
import { mapGetters, mapActions } from 'vuex';

const TEMPLATES = {
  policy: {
    sv: { url: '/documents/sv/Brandskyddspolicy.docx', filename: 'Mall_Brandskyddspolicy.docx' },
    en: { url: '/documents/en/Template_Fire_Safety_Policy.docx', filename: 'Template_Fire_Safety_Policy.docx' },
    fi: { url: '/documents/fi/Malli_Paloturvallisuuspolitiikka.docx', filename: 'Malli_Paloturvallisuuspolitiikka.docx' }
  },
  rules: {
    sv: { url: '/documents/sv/Regler_och_rutiner.docx', filename: 'Mall_Regler_och_rutiner.docx' },
    en: { url: '/documents/en/Template_Rules_and_Procedures.docx', filename: 'Template_Rules_and_Procedures.docx' },
    fi: { url: '/documents/fi/Malli_säännöt_ja_rutiinit.docx', filename: 'Malli_säännöt_ja_rutiinit.docx' }
  },
  business: {
    sv: { url: '/documents/sv/Verksamhetsbeskrivning.docx', filename: 'Mall_Verksamhetsbeskrivning.docx' },
    en: { url: '/documents/en/Template_Business_Description.docx', filename: 'Template_Business_Description.docx' },
    fi: { url: '/documents/fi/Malli_Toimintakuvaus.docx', filename: 'Malli_Toimintakuvaus.docx' }
  }
};

function normalizeLocale(i18n) {
  const raw = (i18n && i18n.locale) || 'sv';
  return String(raw).toLowerCase().split('-')[0]; // 'sv-SE' -> 'sv'
}

export default {
  components: { Pagination },

  props: { files: Array },

  data() {
    return { file: null };
  },

  computed: {
    ...mapGetters('fileStore', ['pagination']),
    ...mapGetters('appStore', ['isMobile']),
    fileName() {
      return this.file ? this.file.name : this.$t('file_list.no_file_selected');
    },
    currentLocale() {
      return normalizeLocale(this.$i18n);
    }
  },

  methods: {
    ...mapActions('fileStore', ['setCurrentPage', 'setPageSize']),

    onDelete(file) { this.$emit('delete', file); },
    onEdit(file) { this.$emit('edit', file); },
    paginate(page) { this.setCurrentPage(page); this.$emit('search'); },
    updatePageSize(size) { this.setPageSize(size); this.setCurrentPage(1); this.$emit('search'); },
    uploadFile(e) { this.file = e.target.files[0]; },
    saveFile() { this.$emit('save', this.file); this.file = null; this.reset(); },
    reset() {
      const input = this.$refs.fileupload;
      input.type = 'text';
      input.type = 'file';
    },

    async downloadLocalized(templateKey) {
      const perLocale = TEMPLATES[templateKey];
      const { url, filename } = perLocale[this.currentLocale] || perLocale.sv;
      await this.downloadTemplate(url, filename);
    },

    async downloadTemplate(url, fileName) {
      try {
        const safeUrl = encodeURI(url);
        const res = await fetch(safeUrl, { credentials: 'same-origin' });
        if (!res.ok) throw new Error(`HTTP ${res.status} for ${safeUrl}`);

        const blob = await res.blob();
        const urlExt = (safeUrl.split('.').pop() || '').toLowerCase();
        const hasExt = fileName.toLowerCase().endsWith(`.${urlExt}`);
        const finalName = hasExt ? fileName : `${fileName}.${urlExt || 'docx'}`;

        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = finalName;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objectUrl);
      } catch (err) {
        console.error('Download failed:', err);
        alert(this.$t ? this.$t('errors.download_failed') : 'Download failed. File not found or server error.');
      }
    }
  }
};
</script>


<style lang="scss" scoped>
.title-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  width: 100%;

  > .title {
	white-space: nowrap;
	font-size: 24px;
	line-height: 2.5rem;
	margin-bottom: 0 !important;
	min-width: 200px;
  }

  &.mobile {
    flex-wrap: wrap;
    .file {
      width: calc(100vw - 1.2rem);
    }
  }
  .file {
    min-width: 280px;
    max-width: 400px;
  }
  .field {
    display: flex;
	flex-wrap: wrap;
    .button {
      border-radius: 2px;
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
  }
  .file-name {
    border-radius: 0;
  }
}
.template-wrapper {
  display: flex;
  flex-wrap: wrap;
  margin-right: 100px;

  &__header {
	margin-left: 0;
	margin-right: 1rem;
	line-height: 2.5rem;
  }

  .button {
    margin-right: 5px;
	margin-bottom: .5rem;

    .fas {
      margin-left: 5px;
    }
  }
}
</style>
